import * as React from "react";
import LegalPage from "./LegalPage";
import type { LegalSection } from "./LegalPage";
import { CONTACT_EMAIL, LEGAL_LAST_UPDATED, OPERATOR_ADDRESS, OPERATOR_NAME } from "./legalDetails";

const intro =
  `These Terms & Conditions govern your use of Stellaris Mission Control, a free workshop tool operated by ${OPERATOR_NAME}. ` +
  "By creating an account or using the platform, you agree to these terms. If you do not agree, please do not use the platform.";

const sections: LegalSection[] = [
  {
    title: "1. What the platform is",
    body: [
      `Stellaris Mission Control is operated by ${OPERATOR_NAME}, ${OPERATOR_ADDRESS}.`,
      "The platform is a companion tool for our workshops, whether they take place online or on site. It lets participants work in groups on imaginary space missions: planning a mission, putting together its hardware, and sending a command to a satellite.",
      "Everything on the platform is a simulation. The missions, the hardware and the satellites are imaginary, and no command is sent to any real spacecraft or system.",
      "The content is meant for learning and for the workshop only. It is not engineering, technical or professional advice.",
    ],
  },
  {
    title: "2. Who may use the platform",
    body: [
      "There is no age limit for using the platform.",
      "If you are a minor, you may only use the platform if a parent or legal guardian has agreed to your use of it and to these terms.",
    ],
  },
  {
    title: "3. Your account",
    body: [
      "To use the platform you register with an email address and a password. Giving your first and last name is optional.",
      "Please use an email address that belongs to you, keep your password to yourself, and do not let other people use your account. You are responsible for what happens under your account.",
      `If you can no longer access your account, contact us at ${CONTACT_EMAIL}.`,
    ],
  },
  {
    title: "4. Free of charge",
    body: [
      "The platform is completely free. There are no payments, subscriptions or paid features.",
      "Because the platform is provided free of charge as an addition to our workshops, you have no claim to it being available at any particular time or to any particular feature.",
    ],
  },
  {
    title: "5. What you enter",
    body: [
      "During a workshop you and your group enter answers and choices for an imaginary mission on a shared sheet, for example payload choices, risks and mitigations.",
      "These entries belong to your group, not to you personally. They are shared: every member of your group can see and change them, and the workshop organisers can access them as operators of the platform.",
      "To make working together easier, the other members of your group can see your email address next to the entries you were the last to change.",
      "Please do not enter personal information about yourself or anyone else, or anything confidential, into the mission fields.",
      "You allow us to store and display what you enter for the purpose of running the workshop.",
    ],
  },
  {
    title: "6. Acceptable use",
    body: [
      "Use the platform only for taking part in a workshop.",
      "Do not try to access other people's accounts, disrupt the platform, get around its access controls, or use it for anything unlawful.",
      "Do not enter content that is offensive, unlawful, or infringes the rights of others.",
      "We may remove content or accounts that break these rules.",
    ],
  },
  {
    title: "7. Availability and changes to the platform",
    body: [
      "We may change, interrupt or discontinue the platform or parts of it at any time, for example for maintenance or between workshops.",
      "Mission content may be reset or removed, for example when a workshop ends.",
    ],
  },
  {
    title: "8. Liability",
    body: [
      "The platform is provided as it is, free of charge, as a learning aid. We do not promise that it is free of errors or always available.",
      "We are fully liable for damage caused by intent or gross negligence, and for injury to life, body or health.",
      "Beyond that, our liability is excluded to the extent permitted by law.",
    ],
  },
  {
    title: "9. Ending your use and deleting your account",
    body: [
      "You can stop using the platform at any time.",
      `To have your account deleted, write to ${CONTACT_EMAIL}. We delete it within 7 working days of your request.`,
      "Accounts that have not been used for one year are deleted.",
    ],
  },
  {
    title: "10. Changes to these terms",
    body: [
      "We may update these terms from time to time. The current version is always available on this page, with the date of the last update at the top.",
      "If you continue to use the platform after a change, the updated terms apply.",
    ],
  },
  {
    title: "11. Governing law",
    body: [
      "These terms are governed by German law.",
      "If you use the platform as a consumer, mandatory consumer protection rules of the country you live in remain unaffected.",
    ],
  },
  {
    title: "12. Contact",
    body: [`${OPERATOR_NAME}, ${OPERATOR_ADDRESS}`, `Email: ${CONTACT_EMAIL}`],
  },
];

export default function TermsOfService(props: { handlePageChange: (page: string) => void }) {
  return (
    <LegalPage
      title="Terms & Conditions"
      updated={LEGAL_LAST_UPDATED}
      intro={intro}
      sections={sections}
      handlePageChange={props.handlePageChange}
    />
  );
}
